import { defineStore } from "pinia";
import PaymentService from "~/services/PaymentService";

type WalletDetails = Record<string, any> | null;

type Wallet = {
  balance: number;
  details: WalletDetails;
  [key: string]: any;
};

export const useWalletStore = defineStore("wallet", () => {
  const wallet: Ref<Wallet | null> = ref(null);
  const isLoading: Ref<boolean> = ref(false);

  const balance = computed<number>(() => {
    const val = wallet.value?.balance;
    if (typeof val === "number") return val;
    const parsed = Number(val);
    return Number.isFinite(parsed) ? parsed : 0;
  });

  const hasWallet = computed<boolean>(() => Boolean(wallet.value));
  const walletId = computed<number | undefined>(() => wallet.value?.id);
  // type приходит на корне wallet (не внутри details). Используем его как primary,
  // с fallback на details.type для совместимости с устаревшими ответами.
  const walletType = computed<number | undefined>(
    () => wallet.value?.type ?? wallet.value?.details?.type
  );
  const hasLegalDetails = computed<boolean>(
    () => walletType.value === 2 || walletType.value === 3
  );
  // Физ.лицо — type 1. Если тип не задан, считаем не физ.лицом (даём заполнить реквизиты).
  const isIndividual = computed<boolean>(() => walletType.value === 1);
  // Полностью заполнены реквизиты ИП/Юр.лица: есть type 2/3 и расчётный счёт.
  const hasFullLegalDetails = computed<boolean>(() => {
    if (walletType.value !== 2 && walletType.value !== 3) return false;
    return Boolean(wallet.value?.details?.bankAccount?.account);
  });

  async function fetchWallet() {
    if (!import.meta.client) return;
    try {
      isLoading.value = true;
      const response = await PaymentService.getWallet();
      wallet.value = (response as Wallet) ?? null;
    } catch (error) {
      console.error("Не удалось загрузить баланс:", error);
    } finally {
      isLoading.value = false;
    }
  }

  function setBalance(value: number) {
    if (!wallet.value) {
      wallet.value = { balance: value, details: null };
    } else {
      wallet.value.balance = value;
    }
  }

  return {
    wallet,
    balance,
    isLoading,
    hasWallet,
    walletId,
    hasLegalDetails,
    isIndividual,
    hasFullLegalDetails,
    fetchWallet,
    setBalance,
  };
});
